import React from "react";
import { AlarmClock, ShieldCheck, Rocket, Repeat } from "lucide-react";

const ymd = (v) => (v ? String(v).slice(0, 10) : null);
const localDate = (v) => {
  const s = ymd(v);
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

// Date-only fields (due dates) — never shifted by the viewer's time zone.
export const fmtDay = (v, withYear = false) => {
  const d = localDate(v);
  return d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(withYear ? { year: "numeric" } : {}) }) : "—";
};

export const deadlineFlag = (clientDue) => {
  const d = localDate(clientDue);
  if (!d) return null;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const days = Math.round((d - t) / 86400000);
  return days < 0 ? "past" : days === 0 ? "today" : days === 1 ? "tomorrow" : null;
};
// J: the internal date is our own target — a soft amber reminder, never "late"
export const internalFlag = (internal) => ({ past: "internal_passed", today: "internal_today", tomorrow: "internal_tomorrow" }[deadlineFlag(internal)] || null);

const FLAG = {
  past: ["Late — client due passed", "bg-rose-50 text-rose-700 border-rose-200"],
  today: ["Client due today", "bg-orange-50 text-orange-700 border-orange-200"],
  tomorrow: ["Client due tomorrow", "bg-amber-50 text-amber-700 border-amber-200"],
  internal_passed: ["Internal target passed", "bg-amber-50 text-amber-700 border-amber-200"],
  internal_today: ["Internal target today", "bg-amber-50 text-amber-700 border-amber-200"],
  internal_tomorrow: ["Internal target tomorrow", "bg-amber-50 text-amber-700 border-amber-200"],
};
const CHIP = "inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-0.5 border whitespace-nowrap";

export function DeadlineChip({ flag, internalFlag: iflag, sentOnTime, testid }) {
  if (sentOnTime != null) {
    return <span data-testid={testid} className={`${CHIP} ${sentOnTime ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`} title="Judged against the client due date for this round">
      <AlarmClock className="w-2.5 h-2.5" /> {sentOnTime ? "On time" : "Late"}</span>;
  }
  const f = FLAG[flag] ? flag : FLAG[iflag] ? iflag : null;
  if (!f) return null;
  return <span data-testid={testid} className={`${CHIP} ${FLAG[f][1]}`}><AlarmClock className="w-2.5 h-2.5" /> {FLAG[f][0]}</span>;
}

export function RoundChip({ revision, sentAt, testid }) {
  if (!revision) return null;
  return (
    <span data-testid={testid} className={`${CHIP} bg-slate-50 text-slate-600 border-slate-200`} title="Pricing round">
      <Repeat className="w-2.5 h-2.5" /> Round {revision}{sentAt ? ` sent ${fmtDay(sentAt)}` : ""}
    </span>
  );
}

export function SignoffChip({ names, testid }) {
  if (!names || names.length === 0) return null;
  return (
    <span data-testid={testid} className={`${CHIP} bg-[#003E87]/10 text-[#003E87] border-[#003E87]/30`}>
      <ShieldCheck className="w-2.5 h-2.5" /> Waiting on {names.join(", ")}
    </span>
  );
}

export function RocketFuelLink({ url, testid, label = false }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={testid} onClick={(e) => e.stopPropagation()} title="Open the RocketFuel account"
      className="inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-0.5 border border-orange-200 text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors">
      <Rocket className="w-2.5 h-2.5" />{label ? " RocketFuel" : ""}
    </a>
  );
}

export function DueDates({ due, internal, testid }) {
  if (!due && !internal) return null;
  return (
    <span data-testid={testid} className="text-[11px] text-slate-500 whitespace-nowrap">
      Client {fmtDay(due)} · Internal {fmtDay(internal)}
    </span>
  );
}
